/* ========================================================================== *\
	IMPORTS
\* ========================================================================== */
import { merge } from 'lodash';
import { isToday } from 'js-date-helper';



/* ========================================================================== *\
	PRIVATE VARIABLES
\* ========================================================================== */
const
	defaultOptions = {
		weekend: [0, 6]
	};



/* ========================================================================== *\
	TYPE DEFINITIONS
\* ========================================================================== */

/**
 * @typedef {Object} DateMetadata
 *
 * @property {Date} date The date for which the metadata has been generated.
 * @property {DateParts} dateParts The individual parts of the date split out.
 * @property {boolean} isToday True when the date is for the current day;
 *           otherwise it is false.
 * @property {boolean} isWeekend True when the date falls in the weekend;
 *           otherwise it is false.
 * @property {number} timestamp A timestamp for year, month, and date of the
 *           provided date.
 */

/**
 * @typedef {Object} DateParts
 *
 * @property {number} date
 * @property {number} day
 * @property {number} month
 * @property {number} year
 */

/**
 * @typedef {Object} MetadataOptions
 *
 * @property {Array<number>} weekend array with the days which make up
 *           the weekend.
 */



/* ========================================================================== *\
	PUBLIC API
\* ========================================================================== */

/**
 * Creates metadata for a given date.
 *
 * @param {Date} date The date to create the metadata for.
 * @param {MetadataOptions} [options] Options to use when generating the
 *        metadata, this can be used to make the metadata locale aware. The
 *        specified options will overwrite the default options.
 *
 * @returns {DateMetadata} The metadata generated for the provided date.
 */
function generateDateMetadata(date, options) {
	const
		{ weekend } = merge(
			{},
			defaultOptions,
			options
		);

	return {
		date: new Date(date),
		dateParts: {
			date: date.getDate(),
			day: date.getDay(),
			month: date.getMonth(),
			year: date.getFullYear()
		},
		isToday: isToday(date),
		isWeekend: weekend.includes(date.getDay()),
		timestamp: date.getTime()
	};
}



/* ========================================================================== *\
	EXPORTS
\* ========================================================================== */

export {
	generateDateMetadata
};
